import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiRefreshCw, FiInfo } from 'react-icons/fi';
import axios from 'axios';
import ImageUploader from '../components/ImageUploader';
import ProcessingLoader from '../components/ProcessingLoader';
import ResultDisplay from '../components/ResultDisplay';
import './ToolPage.css';

const FormatConversion = () => {
  const [file, setFile] = useState(null);
  const [originalPreview, setOriginalPreview] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [targetFormat, setTargetFormat] = useState('png');

  const formats = [
    { value: 'png', label: 'PNG', description: 'Lossless, supports transparency' },
    { value: 'jpg', label: 'JPG', description: 'Smaller file size, no transparency' },
    { value: 'webp', label: 'WebP', description: 'Modern format, best compression' },
    { value: 'gif', label: 'GIF', description: 'Animated images, limited colors' },
    { value: 'bmp', label: 'BMP', description: 'Uncompressed bitmap' },
    { value: 'tiff', label: 'TIFF', description: 'High quality, large files' }
  ];

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setOriginalPreview(selectedFile ? URL.createObjectURL(selectedFile) : null);
    setProcessedImage(null);
    setError(null);
  };

  const handleProcess = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('format', targetFormat);

    try {
      const response = await axios.post('/api/convert', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });

      const imageUrl = URL.createObjectURL(response.data);
      setProcessedImage(imageUrl);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to convert image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!processedImage) return;
    
    const link = document.createElement('a');
    link.href = processedImage;
    link.download = `converted-${Date.now()}.${targetFormat}`;
    link.click();
  };

  const handleReset = () => {
    setFile(null);
    setOriginalPreview(null);
    setProcessedImage(null);
    setError(null);
  };

  return (
    <div className="tool-page">
      {isProcessing && (
        <ProcessingLoader 
          message="Converting image..."
          subMessage={`Converting to ${targetFormat.toUpperCase()} format`}
        />
      )}

      <div className="tool-hero">
        <div className="container">
          <motion.div 
            className="tool-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="tool-icon gradient-accent">
              <FiRefreshCw />
            </div>
            <h1>Format Conversion</h1>
            <p>Convert between JPG, PNG, WebP, GIF, and more. Fast, free, and easy to use.</p>
          </motion.div>
        </div>
      </div>

      <div className="tool-content">
        <div className="container">
          {!processedImage ? (
            <motion.div 
              className="tool-upload-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ImageUploader 
                onFileSelect={handleFileSelect}
                title="Upload image to convert"
                subtitle="Supports all common image formats"
              />

              {file && (
                <motion.div 
                  className="options-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <label className="option-label">Convert to:</label>
                  <div className="format-grid">
                    {formats.map((format) => (
                      <button
                        key={format.value}
                        className={`format-option ${targetFormat === format.value ? 'selected' : ''}`}
                        onClick={() => setTargetFormat(format.value)}
                      >
                        <span className="format-name">{format.label}</span>
                        <span className="format-desc">{format.description}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {file && (
                <motion.div 
                  className="process-actions"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <button 
                    className="btn-process"
                    onClick={handleProcess}
                    disabled={isProcessing}
                  >
                    Convert to {targetFormat.toUpperCase()}
                  </button>
                </motion.div>
              )}

              {error && (
                <motion.div 
                  className="error-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <ResultDisplay
              originalImage={originalPreview}
              processedImage={processedImage}
              onDownload={handleDownload}
              onReset={handleReset}
            />
          )}

          <div className="tool-info-section">
            <div className="info-card">
              <FiInfo className="info-icon" />
              <h3>Format Guide</h3>
              <p><strong>PNG:</strong> Best for images with transparency or text. <br/>
                 <strong>JPG:</strong> Best for photographs, smaller file size. <br/>
                 <strong>WebP:</strong> Modern format with excellent compression. <br/>
                 <strong>GIF:</strong> Best for simple animations.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormatConversion;
